/**
 * Sinia Custom Chart
 *
 * Clase base para todos gráficos, desde está clase se hereda la configuración por defecto para los graficos.
 * Aquí estan considerados los colores, fuentes, opciones de impresión, leyendas y otros.
 *
 * La actualización de los graficos se realiza siguiendo las recomendaciones
 * para el optimo funcionamiento de la pagina oficial en GitHub de highcharts-react-official
 * Ver: [https://github.com/highcharts/highcharts-react](https://github.com/highcharts/highcharts-react)
 *
 * @author Jaime Cruz
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import Highcharts from "highcharts";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsExportData from "highcharts/modules/export-data";
import HighchartsReact from "highcharts-react-official";
import HighchartsNoDataToDisplay from "highcharts/modules/no-data-to-display";
import StringUtils from "../../commons/string-utils.module";

import "./sinia-custom-chart.component.scss";

Highcharts.SVGRenderer.prototype.symbols.download = function(x, y, w, h) {
  var path = [
    // Arrow stem
    "M",
    x + w * 0.5,
    y,
    "L",
    x + w * 0.5,
    y + h * 1,
    // Arrow head
    "M",
    x + w * 0.3,
    y + h * 0.5,
    "L",
    x + w * 0.5,
    y + h * 0.7,
    "L",
    x + w * 0.7,
    y + h * 0.5,
    // Box
    "M",
    x,
    y + h * 0.9,
    "L",
    x,
    y + h,
    "L",
    x + w,
    y + h,
    "L",
    x + w,
    y + h * 0.9,
  ];
  return path;
};

// Permite mostrar un mensaje cuando no hay datos que mostrar.
HighchartsNoDataToDisplay(Highcharts);
/** Principales tipos de gráficos */
const CHART_TYPE_COLUMN = "column";
const CHART_TITLE_FONT_SIZE = "14px";

/** Colores characteristicos de SINIA */
const SINIA_BLUE = "#79b340";
const SINIA_GREEN = "#79b340";

/** Valores por defecto para todos los gráficos */
const DEFAULT_CHART_OPTIONS = {
  chart: {
    type: CHART_TYPE_COLUMN,
  },
  colors: [SINIA_BLUE, SINIA_GREEN],
  credits: {
    text: "Fuente: DGECIA (MINAM)",
    href: "",
  },
  lang: {
    noData:
      "<div class='text-center'><i class='fa fa-info-circle h4'></i><br>No se dispone de información para el ámbito seleccionado.</div>", //the text to be displayed
  },
  noData: {
    position: {
      align: "center",
      verticalAlign: "middle",
    },
    style: {
      color: SINIA_BLUE,
      fontWeight: "100",
      fontSize: "13px",
    },
    useHTML: true,
  },
  plotOptions: {
    bar: {
      dataLabels: {
        enabled: true,
      },
      enableMouseTracking: true,
    },
    line: {
      dataLabels: {
        enabled: true,
      },
      enableMouseTracking: true,
    },
    column: {
      dataLabels: {
        enabled: true,
      },
      enableMouseTracking: true,
    },
    area: {
      dataLabels: {
        enabled: true,
      },
      enableMouseTracking: true,
    },
    series: {
      dataLabels: {
        enabled: true,
        style: {
          color: "#696969"
        },
      },
    },
  },
  legend: {
    enabled: false,
  },
};
class SiniaCustomChart extends Component {
  static propTypes = {
    /** Tipo de gráfico */
    chartType: PropTypes.string,

    /** Titulo del gráfico */
    title: PropTypes.string,

    /**
     * Title Template
     * Ejm: 'Hectareas reforestadas por mes desde {mesInicio} hasta {mesFin} en la región {region}
     * debe ser usado con  titleTplData
     */
    titleTpl: PropTypes.string,
    /**
     * Title Template Data
     * Ejm: {
     *        mesInicio: 'Enero',
     *        mesFin: 'Setiembre',
     *        region: 'Tumbes'
     *      }
     */
    titleTplData: PropTypes.object,
    /** Titulo del eje X */
    xAxeTitle: PropTypes.string,

    /** Titulo del eje Y */
    yAxeTitle: PropTypes.string,

    titleRenderer: PropTypes.func,
    /**
     * Indica si el gráfico admite multiples series:
     * por defecto solo admite una serie
     */
    multiSeries: PropTypes.bool,
    /**
     * Si tendrá opciones de exportación
     * false por defecto
     */
    exportable: PropTypes.bool,
    /**
     * La que se usará para renderizar el gráfico
     * @type {Array} data
     */
    data: PropTypes.array,
    /**
     * La fuente que proporciona los datos
     * @type {String} data
     */
    source: PropTypes.string,
  };
  static defaultProps = {
    chartType: CHART_TYPE_COLUMN,
    titleTpl: null,
    titleTplData: {},
    title: "Título de gráfico",
    xAxeTitle: "Eje X",
    yAxeTitle: "Eje Y",
    titleRenderer: null,
    multiSeries: false,
    exportable: false,
    data: [],
    source: "",
  };
  constructor(props) {
    super(props);
    let me = this;
    props = me.props;
    // Añadir opciones de exportación
    if (props.exportable) {
      me.makeExportable();
    }
    me.setupInitialState();
  }
  setupInitialState() {
    let me = this,
      props = me.props,
      title = me.builTitle();
    /**
     * Guardamos las opciones del gráfico en el state
     * Recomendado por highcharts-react-official
     */
    me.state = {
      chartOptions: {
        ...DEFAULT_CHART_OPTIONS,
        chart: {
          type: props.chartType,
          style: {
            fontFamily: 'Roboto, sans-serif;'
           }
        },
        credits: {
          text: props.source,
          href: "",
        },
        exporting: {
          buttons: {
            first: {
              symbol: "menuball",
              menuItems: [
                {
                  text:
                    "<i class='fas fa-chart-bar chart-type-bar'></i> Ver como Barra",
                  textKey: "chartBart",
                  onclick: function() {
                    me.changeType("column");
                  },
                },
                {
                  text:
                    "<i class='fas fa-chart-line chart-type-line'></i> Ver como Linea",
                  textKey: "downloadPNG",
                  onclick: function() {
                    me.changeType("line");
                  },
                },
                {
                  text:
                    "<i class='fas fa-chart-area chart-type-area'></i> Ver como Area",
                  textKey: "column",
                  onclick: function() {
                    me.changeType("area");
                  },
                },
              ],
            },
            contextButton: {
              symbol: "download",
              menuItems: [
                {
                  text: "<i class='fa fa-print chart-print-icon'></i> Imprimir",
                  textKey: "printChart",
                  onclick: function() {
                    this.print();
                  },
                },
                {
                  text:
                    "<i class='fa fa-file-image chart-export-png-icon'></i> Descargar como PNG",
                  textKey: "downloadPNG",
                  onclick: function() {
                    this.exportChart();
                  },
                },
                {
                  text:
                    "<i class='fa fa-file-excel chart-export-csv-icon'></i> Descargar como CSV",
                  textKey: "downloadCSV",
                  onclick: function() {
                    this.downloadCSV();
                  },
                },
                {
                  text:
                    "<i class='fa fa-file-pdf chart-export-pdf-icon'></i> Descargar como PDF",
                  textKey: "downloadPDF",
                  onclick: function() {
                    this.exportChart({
                      type: "application/pdf",
                    });
                  },
                },
              ],
            },
          },
        },
        title: {
          text: title,
          style: {
            fontSize: CHART_TITLE_FONT_SIZE,
          },
          widthAdjust: -100,
        },
        xAxis: {
          title: {
            text: props.xAxeTitle,
          },
        },
        yAxis: {
          title: {
            text: props.yAxeTitle,
          },
        },
        series: me.getSeriesDef(),
      },
    };
    window.xchart = Highcharts;
  }
  /**
   * Retorna la estructura de series requerida por Highcharts
   */
  getSeriesDef() {
    return [
      {
        data: this.props.data,
      },
    ];
  }
  makeExportable() {
    HighchartsExporting(Highcharts);
    HighchartsExportData(Highcharts);
  }
  builTitle() {
    let props = this.props;
    if (props.titleTpl) {
      return StringUtils.tplApply(props.titleTpl, props.titleTplData);
    }
    if (props.titleRenderer) {
      return props.titleRenderer(this, props);
    }
    return props.title;
  }

  /**
   * @param {array} series
   * @param {array} categorias
   * @param {object} camposTitulo Usar junto con titleTpl en caso de titulos dinámicos
   */
  actualizarDatos = (series, categorias, camposTitulo) => {
    let props = this.props;
    // si el gráfico solo tiene una serie
    if (!props.multiSeries) {
      series = [{ name: props.seriesLabel, data: series }];
    }
    let options = {
      series: series,
      xAxis: {
        categories: categorias,
      },
    };
    if (camposTitulo) {
      options.title = {
        text: StringUtils.tplApply(props.titleTpl, camposTitulo),
      };
    }
    this.setState({ chartOptions: options });
  };

  /**
   * Permite actualizar todas las opciones del gráfico
   */
  actualizarOpciones(opciones) {
    this.setState({ chartOptions: opciones });
  }

  /**
   * Permite actualizar Las categorias del gráfico
   */
  actualizarCategorias(categorias) {
    this.setState({
      chartOptions: {
        xAxis: {
          categories: categorias,
        },
      },
    });
  }

  /**
   * Permite actualizar Los valores del gráfico
   */
  actualizarSeries(series) {
    this.setState({
      chartOptions: {
        series: series,
      },
    });
  }

  /**
   * Permite actualizar el título del gráfico.
   */
  actualizarTitulo(titulo) {
    this.setState({
      chartOptions: {
        title: {
          text: titulo,
        },
      },
    });
  }
  changeType(newType) {
    this.setState({
      chartOptions: {
        chart: {
          type: newType,
        },
      },
    });
  }
  render() {
    const { chartOptions } = this.state;
    return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
  }
}

export default SiniaCustomChart;
